import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import FrontDoor from '@cartwhl/ui/feature/FrontDoor';
import shouldDismissOnOutsideClick from '@cartwhl/ui/hooks/shouldDismissOnOutsideClick';
import useClickOutside from '@cartwhl/ui/hooks/useClickOutside';
import useElementSize from '@cartwhl/ui/hooks/useElementSize';
import CopyIcon from '@cartwhl/ui/icons/CopyIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import TrashIcon from '@cartwhl/ui/icons/TrashIcon';

import {
  type AssetLibraryEmbedSelection,
  AssetLibraryModal,
  createMotionEditorAssetLibraryEmbedConfig,
} from '../../AssetLibrary';
import { PATH } from '../constants';
import { getMotionEditorCreateScenePath } from '../store/sceneCreateUtils';
import { ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES } from '../feature-flags';
import { useKeyPoseFrameCaptureActions } from '../keyPoses/hooks/useKeyPoseCaptureActions';
import { useKeyPoseGenerationActions } from '../keyPoses/hooks/useKeyPoseGenerationActions';
import { useStore } from '../motionEditorStore';
import { useRepathingGenerationActions } from '../repathing';
import { useStore as useRepathingStore } from '../repathing/repathingStore';
import FrontDoorActionBar from './frontDoor/FrontDoorActionBar';

const MAX_ASSET_LIBRARY_WIDTH = 1116;

export type MotionEditorFrontDoorMode = '' | 'motions';

interface MotionEditorFrontDoorProps {
  routeSceneID?: string;
  frontDoorMode: MotionEditorFrontDoorMode;
  onFrontDoorModeChange: (mode: MotionEditorFrontDoorMode) => void;
}

interface KeyPoseFrontDoorProps {
  canRegenerateMotion?: boolean;
  canPastePose: boolean;
  keyPoseCount: number;
  onCopyPose: () => void;
  onAddKeyPose: () => void;
  onPastePose: () => void;
  onRegenerateMotion: () => void;
  onRemoveAllKeyPoses: () => void;
}

interface KeyPoseFrontDoorActionsProps {
  canRegenerateMotion?: boolean;
  onRegenerateMotion: () => void;
}

export default function MotionEditorFrontDoor({
  routeSceneID,
  frontDoorMode,
  onFrontDoorModeChange,
}: MotionEditorFrontDoorProps) {
  const store = useStore();
  const repathingStore = useRepathingStore();
  const navigate = useNavigate();
  const { canRegenerateKeyPoseMotion, regenerateKeyPoseMotion } =
    useKeyPoseGenerationActions();
  const { canRegeneratePathMotion, regeneratePathMotion } =
    useRepathingGenerationActions();
  const { playback, scene } = store.state;
  const hasSelectedScene = Boolean(routeSceneID);
  const hasSelectedMotion = Boolean(playback.selectedSceneMotion);
  const frontDoorRef = useRef<HTMLDivElement>(null);
  const { width: frontDoorWidth, height: frontDoorHeight } =
    useElementSize(frontDoorRef);

  useClickOutside(
    frontDoorRef,
    () => {
      if (frontDoorMode === '') {
        return;
      }
      onFrontDoorModeChange('');
    },
    {
      shouldTrigger: shouldDismissOnOutsideClick,
    },
  );

  const motionTitle = useMemo(() => {
    return scene.data?.sceneName?.trim() || routeSceneID || 'Current scene';
  }, [routeSceneID, scene.data?.sceneName]);
  const assetLibraryPanelStyle =
    frontDoorHeight > 0 && frontDoorWidth > 0
      ? {
          height: `${frontDoorHeight - 16}px`,
          width: `${Math.min(frontDoorWidth - 16, MAX_ASSET_LIBRARY_WIDTH)}px`,
        }
      : undefined;

  const handleAssetLibraryPick = useCallback(
    (selection: AssetLibraryEmbedSelection) => {
      onFrontDoorModeChange('');

      const motionID =
        selection.kind === 'mogen-job'
          ? selection.jobId
          : selection.kind === 'stock-motion'
            ? selection.motionId
            : null;
      if (!motionID) return;

      navigate(getMotionEditorCreateScenePath(motionID, selection.label));
    },
    [navigate, onFrontDoorModeChange],
  );

  const handleOpenSceneFromLibrary = useCallback(
    (sceneId: string) => {
      onFrontDoorModeChange('');
      navigate(`${PATH}/${encodeURIComponent(sceneId)}`);
    },
    [navigate, onFrontDoorModeChange],
  );

  const assetLibraryEmbedConfig = useMemo(
    () =>
      createMotionEditorAssetLibraryEmbedConfig({
        onPick: handleAssetLibraryPick,
        onOpenScene: handleOpenSceneFromLibrary,
      }),
    [handleAssetLibraryPick, handleOpenSceneFromLibrary],
  );

  return (
    <FrontDoor.Container ref={frontDoorRef} size="sm">
      <FrontDoor.FadeContent show={frontDoorMode === 'motions'}>
        <div
          className="flex min-h-0 w-full max-w-[1116px] flex-1 flex-col self-center"
          style={assetLibraryPanelStyle}
        >
          <AssetLibraryModal
            maxWidth={MAX_ASSET_LIBRARY_WIDTH}
            embedConfig={assetLibraryEmbedConfig}
            onClose={() => onFrontDoorModeChange('')}
          />
        </div>
      </FrontDoor.FadeContent>

      <FrontDoor.FadeContent show={frontDoorMode === ''} className="w-full">
        {hasSelectedScene ? (
          scene.status === 'ready' && scene.data ? (
            ENABLE_MOTION_EDITOR_KEY_POSE_SURFACES ? (
              hasSelectedMotion ? (
                <MotionEditFrontDoorActions
                  canRegenerateKeyPoseMotion={canRegenerateKeyPoseMotion}
                  canRegeneratePathMotion={canRegeneratePathMotion}
                  controlsMode={repathingStore.state.controlsMode}
                  onRegenerateKeyPoseMotion={() => {
                    void regenerateKeyPoseMotion();
                  }}
                  onRegeneratePathMotion={() => {
                    void regeneratePathMotion();
                  }}
                />
              ) : (
                <AssetLibraryFrontDoorButton
                  onOpenAssetLibrary={() => onFrontDoorModeChange('motions')}
                />
              )
            ) : (
              <FrontDoorActionBar
                motionTitle={motionTitle}
                onOpenAssetLibrary={() => onFrontDoorModeChange('motions')}
              />
            )
          ) : (
            <FrontDoor.Button
              variant="secondary"
              text={
                scene.status === 'error'
                  ? 'Failed to load scene'
                  : 'Loading scene…'
              }
              disabled={true}
              rounded="both"
            />
          )
        ) : (
          <AssetLibraryFrontDoorButton
            onOpenAssetLibrary={() => onFrontDoorModeChange('motions')}
          />
        )}
      </FrontDoor.FadeContent>
    </FrontDoor.Container>
  );
}

function MotionEditFrontDoorActions({
  canRegenerateKeyPoseMotion,
  canRegeneratePathMotion,
  controlsMode,
  onRegenerateKeyPoseMotion,
  onRegeneratePathMotion,
}: {
  canRegenerateKeyPoseMotion?: boolean;
  canRegeneratePathMotion?: boolean;
  controlsMode: 'body' | 'path';
  onRegenerateKeyPoseMotion: () => void;
  onRegeneratePathMotion: () => void;
}) {
  if (controlsMode === 'path') {
    return (
      <RegenerateMotionFrontDoorButton
        canRegenerateMotion={canRegeneratePathMotion}
        onRegenerateMotion={onRegeneratePathMotion}
      />
    );
  }

  return (
    <KeyPoseFrontDoorActions
      canRegenerateMotion={canRegenerateKeyPoseMotion}
      onRegenerateMotion={onRegenerateKeyPoseMotion}
    />
  );
}

function RegenerateMotionFrontDoorButton({
  canRegenerateMotion,
  onRegenerateMotion,
}: KeyPoseFrontDoorActionsProps) {
  return (
    <FrontDoor.Button
      variant="primary"
      text="Regenerate motion"
      icon={<RegenerateGlyph />}
      rounded="both"
      disabled={!canRegenerateMotion}
      onClick={onRegenerateMotion}
    />
  );
}

function KeyPoseFrontDoorActions({
  canRegenerateMotion,
  onRegenerateMotion,
}: KeyPoseFrontDoorActionsProps) {
  const {
    addKeyPoseAtCurrentFrame,
    canPasteKeyPose,
    copySourceFrame,
    getCurrentFrame,
    keyPoseCount,
    pasteKeyPoseAtFrame,
    removeAllKeyPoses,
  } = useKeyPoseFrameCaptureActions();

  const handleCopyPose = useCallback(() => {
    copySourceFrame(getCurrentFrame());
  }, [copySourceFrame, getCurrentFrame]);
  const handlePastePose = useCallback(() => {
    pasteKeyPoseAtFrame(getCurrentFrame());
  }, [getCurrentFrame, pasteKeyPoseAtFrame]);

  return (
    <KeyPoseFrontDoor
      canRegenerateMotion={canRegenerateMotion}
      canPastePose={canPasteKeyPose}
      keyPoseCount={keyPoseCount}
      onAddKeyPose={addKeyPoseAtCurrentFrame}
      onCopyPose={handleCopyPose}
      onPastePose={handlePastePose}
      onRegenerateMotion={onRegenerateMotion}
      onRemoveAllKeyPoses={removeAllKeyPoses}
    />
  );
}

function AssetLibraryFrontDoorButton({
  onOpenAssetLibrary,
}: {
  onOpenAssetLibrary: () => void;
}) {
  return (
    <FrontDoor.Button
      variant="primary"
      text="Add Motion"
      icon={<PlusIcon className="h-4 w-4" />}
      rounded="both"
      isWide
      onClick={onOpenAssetLibrary}
      className="w-full"
    />
  );
}

function KeyPoseFrontDoor({
  canRegenerateMotion,
  canPastePose,
  keyPoseCount,
  onCopyPose,
  onAddKeyPose,
  onPastePose,
  onRegenerateMotion,
  onRemoveAllKeyPoses,
}: KeyPoseFrontDoorProps) {
  const hasKeyPose = keyPoseCount > 0;
  const regenerateEnabled = canRegenerateMotion ?? hasKeyPose;

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <FrontDoor.Button
        variant="primary"
        text="Regenerate motion"
        icon={<RegenerateGlyph />}
        rounded="first"
        disabled={!regenerateEnabled}
        onClick={onRegenerateMotion}
      />
      <Dropdown
        align="center"
        side="top"
        width={290}
        triggerTooltip="More actions"
        trigger={
          <FrontDoor.Button
            aria-label="More actions"
            variant="tertiary"
            icon={<ThreeDotsIcon className="size-4" />}
            rounded="last"
          />
        }
      >
        <Dropdown.Item icon={<PlusIcon />} onClick={onAddKeyPose}>
          Add key pose at current frame
        </Dropdown.Item>
        <Dropdown.Separator />
        <Dropdown.Item icon={<CopyIcon />} onClick={onCopyPose}>
          Copy pose at current frame
        </Dropdown.Item>
        <Dropdown.Item
          disabled={!canPastePose}
          icon={<PasteGlyph />}
          onClick={canPastePose ? onPastePose : undefined}
        >
          Paste pose at current frame
        </Dropdown.Item>
        <Dropdown.Separator />
        <Dropdown.Item
          disabled={!hasKeyPose}
          icon={<TrashIcon className="size-4" />}
          itemClassName="text-red-50"
          onClick={hasKeyPose ? onRemoveAllKeyPoses : undefined}
        >
          Remove all key poses
        </Dropdown.Item>
      </Dropdown>
    </div>
  );
}

function RegenerateGlyph() {
  return (
    <svg
      aria-hidden
      className="size-4"
      fill="none"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="stroke-current"
        d="M15.5 7.5A5.8 5.8 0 0 0 4.4 9.1M4.5 12.5a5.8 5.8 0 0 0 11.1-1.6M15.5 7.5V4.8M15.5 7.5h-2.8M4.5 12.5v2.7M4.5 12.5h2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function PasteGlyph() {
  return (
    <svg
      aria-hidden
      className="size-5"
      fill="none"
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="stroke-current"
        d="M7.5 4.5h5M7 6.5H5.8c-.7 0-1.3.6-1.3 1.3v7.4c0 .7.6 1.3 1.3 1.3h8.4c.7 0 1.3-.6 1.3-1.3V7.8c0-.7-.6-1.3-1.3-1.3H13M7 4.5c0-.6.4-1 1-1h4c.6 0 1 .4 1 1v2H7v-2ZM10 9.5v4M8 11.5h4"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}
